import { type FC, useEffect, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { Button, Modal, Text } from '../../../ui-components';
import { maxLength } from '../../../utils/validation';
import { Field } from '../../field';
import type { Photo } from '../types';

export interface PhotoModalProps {
    isOpen: boolean;
    onClose: () => void;
    files: FileList | null;
    onSubmit: (photos: Photo[]) => void;
}
export const PhotoModal: FC<PhotoModalProps> = ({ isOpen, onClose, files, onSubmit }) => {
    const intl = useIntl();
    const [b64Files, setB64Files] = useState<string[]>([]);
    const form = useForm({ reValidateMode: 'onChange', mode: 'onChange' });
    const convertBase64 = (file: File): Promise<string | ArrayBuffer | null> =>
        new Promise((resolve, reject) => {
            const fileReader = new FileReader();
            fileReader.readAsDataURL(file);

            fileReader.onload = (): void => {
                resolve(fileReader.result);
            };

            fileReader.onerror = (error): void => {
                reject(error);
            };
        });

    useEffect(() => {
        const processFiles = async (): Promise<void> => {
            if (files) {
                try {
                    const processedFiles = await Promise.all(
                        Array.from(files).map(
                            async (file) => (await convertBase64(file)) as string,
                        ),
                    );
                    setB64Files(() => [...processedFiles]);
                } catch (error) {
                    console.error('Error processing files:', error);
                }
            }
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        processFiles();
    }, []);

    if (!files) {
        return null;
    }

    const preSubmit = (): void => {
        const errorsLength = Object.keys(form.formState.errors).length;
        if (errorsLength) {
            return;
        }

        const photosData: Photo[] = b64Files.map(
            (file, index) =>
                ({
                    id: uuidv4(),
                    caption: form.getValues()[`${index}-caption`] as string,
                    b64data: file,
                    type: 'photo',
                }) as unknown as Photo,
        );

        onSubmit(photosData);
        onCloseModal();
    };

    const onCloseModal = (): void => {
        form.reset();
        setB64Files([]);
        onClose();
    };

    return (
        <Modal
            title={intl.formatMessage({
                id: 'turn-to.photo-modal.add-caption',
                defaultMessage: 'Add a caption',
            })}
            id="photo-modal"
            isOpen={isOpen}
            onClose={onCloseModal}
            variant="turn-to"
        >
            {b64Files.map((file, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Flex key={index} gap={4} flexWrap="wrap">
                    <Box>
                        <img src={file} alt="uploaded pic" />
                    </Box>
                    <Field
                        type="textarea"
                        name={`${index}-caption`}
                        control={form.control}
                        label={intl.formatMessage({
                            defaultMessage: 'Caption (optional)',
                            id: 'turn-to.photo-modal.caption',
                        })}
                        helpText={
                            <Text size="small">
                                <FormattedMessage
                                    id="turn-to.photo-modal.caption-helper"
                                    defaultMessage="Please describe this media in 100 characters or less"
                                />
                            </Text>
                        }
                        error={form.formState.errors[`${index}-caption`]}
                        rules={{
                            maxLength: {
                                value: maxLength.caption,
                                message: intl.formatMessage(
                                    {
                                        defaultMessage: 'No more than {max} characters',
                                        id: 'error.field.maxLength',
                                    },
                                    {
                                        max: maxLength.caption,
                                    },
                                ),
                            },
                        }}
                    />
                </Flex>
            ))}
            <Flex gap={2} marginY={4}>
                <Button variant="primary-black" onClick={preSubmit} wide>
                    <FormattedMessage id="turn-to.photo-modal.next" defaultMessage="Next" />
                </Button>
                <Button variant="secondary" onClick={preSubmit} wide>
                    <FormattedMessage id="turn-to.photo-modal.cancel" defaultMessage="Cancel" />
                </Button>
            </Flex>
            <Text size="small">
                <FormattedMessage
                    id="turn-to.photo-modal.next-description"
                    defaultMessage='By choosing "Next" your visuals will be submitted to our product gallery, even if you decide not to submit your review.'
                />
            </Text>
            <Text size="small">
                <FormattedMessage
                    id="turn-to.photo-modal.submission-description"
                    defaultMessage="Your submission is subject to our terms of use. Do not link or upload copyrighted material without the copyright owner’s permission."
                />
            </Text>
        </Modal>
    );
};
